import {
    Edit,
    SimpleForm,
    TextInput,
    required,
    Toolbar,
    SaveButton,
    useNotify,
    useRedirect,
    ImageInput,
    ImageField,
    SelectInput,
    useRecordContext,
} from "react-admin";
import { Box, Button, Typography } from "@mui/material";
import useAuthorize from "../hooks/useAuthorize";
import { NEWS_PORTAL_ROLES } from "./newsRoles";

const CustomToolbar = () => {
    const redirect = useRedirect();

    const handleBack = () => {
        redirect("/blogs");
    };

    return (
        <Toolbar>
            <SaveButton />
            <Button color="primary" onClick={handleBack} style={{ marginLeft: "10px" }}>
                Back
            </Button>
        </Toolbar>
    );
};

// Image Validation Function
const validateImageSizeAndType = (value: any): string | undefined => {
    console.log("Received value:", value);

    const file = value?.rawFile;

    if (!file) {
        console.log("No file provided");
        return undefined;
    }

    console.log("Extracted file:", file);

    const maxSizeInBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
        console.log("File size exceeded");
        return "Image size should be a maximum of 5 MB";
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
        console.log("Invalid file type");
        return "Only JPG, JPEG, PNG, GIF, or SVG files are allowed.";
    }

    return undefined;
};

/** Read-only created / published timestamps (CRE-30). */
function BlogDateFields() {
    const record = useRecordContext();
    if (!record) return null;

    const formatDate = (value?: string | null) => {
        if (!value) return 'Not set';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'Not set';
        return date.toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: 'Asia/Kolkata',
        });
    };

    return (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">
                <strong>Created:</strong> {formatDate(record.created_at)}
            </Typography>
            <Typography variant="body2">
                <strong>Published:</strong>{' '}
                {record.status === 'published' && record.published_at
                    ? formatDate(record.published_at)
                    : 'Not published'}
            </Typography>
        </Box>
    );
}

/** Read-only traceability JSON — from `blogs.notes` (automated generator). Shown at bottom of edit page. */
function BlogNotesPanel() {
    const record = useRecordContext();
    if (!record) return null;

    const raw = record.notes;
    const boxSx = {
        mt: 2,
        mb: 3,
        mx: "auto",
        maxWidth: 960,
        px: 2,
    } as const;

    if (raw == null || raw === "") {
        return (
            <Box sx={boxSx}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Source traceability &amp; notes
                </Typography>
                <Box
                    component="div"
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        p: 2,
                        minHeight: 72,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        No source traceability notes on this article (manual entries typically have none).
                    </Typography>
                </Box>
            </Box>
        );
    }

    const text = typeof raw === "string" ? raw : JSON.stringify(raw, null, 2);

    return (
        <Box sx={boxSx}>
            <Typography variant="subtitle2" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
                Source traceability &amp; notes (AI / automated)
            </Typography>
            <Box
                component="pre"
                sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 420,
                    overflow: "auto",
                    m: 0,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    color: "text.primary",
                    fontSize: "0.8125rem",
                    lineHeight: 1.6,
                    fontFamily:
                        'ui-sans-serif, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
                }}
            >
                {text}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Verify URLs and snippets against the article body. QA output is included for automated drafts.
            </Typography>
        </Box>
    );
}

export const BlogEdit = (props: any) => {
    const notify = useNotify();
    const redirect = useRedirect();
    useAuthorize(NEWS_PORTAL_ROLES);

    const handleSubmit = async (values: any) => {
        try {
            const token = localStorage.getItem("user_authToken");
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("description", values.description.replace(/\n/g, "<br/>"));
            formData.append("status", values.status === "published" ? "published" : "draft");

            if (values.image && values.image.rawFile) {
                formData.append("image", values.image.rawFile);
            } else if (values.existingImage) {
                formData.append("existingImage", values.existingImage);
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/blogs/${values.id}`, {
                method: "PUT",
                body: formData,
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });

            if (!response.ok) {
                throw new Error("Failed to update news article");
            }

            notify("News article updated successfully", { type: "success" });
            redirect("/blogs");
        } catch (error) {
            console.error("Error updating blog:", error);
            notify("Error updating news article", { type: "error" });
        }
    };

    return (
        <Edit {...props}>
            <SimpleForm toolbar={<CustomToolbar />} onSubmit={handleSubmit}>
                <BlogDateFields />
                <TextInput source="source" label="Source" disabled fullWidth />
                <SelectInput
                    source="status"
                    label="Status"
                    choices={[
                        { id: "draft", name: "Draft" },
                        { id: "published", name: "Published" },
                    ]}
                    fullWidth
                />
                <TextInput source="title" label="Title" validate={required()} fullWidth />

                <TextInput
                    source="description"
                    label="Description"
                    validate={required()}
                    multiline
                    fullWidth
                    rows={4}
                    parse={(value) => value.replace(/\n/g, "<br>")}
                    format={(value) => value?.replace(/<br\s*\/?>/g, "\n")}
                />

                <ImageInput
                    source="image"
                    label="Related pictures"
                    accept="image/*"
                    validate={validateImageSizeAndType}
                >
                    <ImageField source="src" title="title" />
                </ImageInput>

                <BlogNotesPanel />
            </SimpleForm>
        </Edit>
    );
};

export default BlogEdit;
